import {
  RODDO_ADMOB,
  canUseNativeAdMob,
  getRewardedSearchLureAdUnitId,
} from '../constants/adMob';
import { auth } from '../lib/firebase';
import { initializeRoddoAdMob } from './adMob';
import { trackRoddoEvent } from './analytics';
import {
  createSearchLureRequestId,
  getRewardedSearchLureEligibility,
  getRewardedSearchLureStatus,
  type RewardedSearchLureStatusResult,
} from './searchLures';

export type RewardedSearchLureAdResult =
  | {
      ok: true;
      earned: true;
      rewardAmount: number;
      rewardType: string;
      testMode: boolean;
      serverStatus?: RewardedSearchLureStatusResult['status'];
      searchLureBalance?: number;
      dailyCount?: number;
      dailyCap?: number;
    }
  | { ok: false; earned: false; reason: string };

let rewardedInFlight = false;

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

export function canOfferRewardedSearchLureAd(): boolean {
  return Boolean(
    RODDO_ADMOB.enabled &&
      RODDO_ADMOB.rewardedSearchLuresEnabled &&
      canUseNativeAdMob() &&
      getRewardedSearchLureAdUnitId(),
  );
}

export function isRewardedSearchLureAdBusy(): boolean {
  return rewardedInFlight;
}

async function waitForVerifiedReward(
  requestId: string,
): Promise<RewardedSearchLureStatusResult> {
  let latest: RewardedSearchLureStatusResult = {
    ok: true,
    status: 'pending',
    requestId,
    grantedAmount: 0,
    dailyCap: 5,
  };

  // SSV callbacks usually arrive quickly, but can be delayed. Keep the UI wait
  // bounded and let the live user-profile subscription reflect a later grant.
  for (let attempt = 0; attempt < 8; attempt += 1) {
    await sleep(attempt === 0 ? 500 : 1000);
    latest = await getRewardedSearchLureStatus(requestId);
    if (latest.status !== 'pending') return latest;
  }

  return latest;
}

export async function showRewardedSearchLureAd(): Promise<RewardedSearchLureAdResult> {
  if (!canOfferRewardedSearchLureAd()) {
    return { ok: false, earned: false, reason: 'rewarded_ads_disabled' };
  }
  if (rewardedInFlight) {
    return { ok: false, earned: false, reason: 'rewarded_ad_already_presenting' };
  }

  const adId = getRewardedSearchLureAdUnitId();
  if (!adId) return { ok: false, earned: false, reason: 'missing_rewarded_ad_unit' };

  rewardedInFlight = true;
  try {
    const initState = await initializeRoddoAdMob();
    if (initState !== 'ready') {
      return { ok: false, earned: false, reason: `admob_${initState}` };
    }

    const isTestMode = RODDO_ADMOB.testMode;
    const uid = auth.currentUser?.uid || '';
    let requestId = '';

    if (!isTestMode) {
      if (!uid) return { ok: false, earned: false, reason: 'rewarded_ad_requires_auth' };

      const eligibility = await getRewardedSearchLureEligibility();
      if (!eligibility.eligible) {
        return { ok: false, earned: false, reason: 'rewarded_daily_cap_reached' };
      }

      requestId = createSearchLureRequestId('rewarded');
    }

    const { AdMob } = await import('@capacitor-community/admob');
    trackRoddoEvent('rewarded_ad_load_started', {
      placement: 'search_lure_reward',
      ad_format: 'rewarded',
      test_mode: isTestMode,
    });

    const loaded = await AdMob.prepareRewardVideoAd({
      adId,
      isTesting: isTestMode,
      ...(!isTestMode
        ? {
            ssv: {
              userId: uid,
              customData: `search_lures:${requestId}`,
            },
          }
        : {}),
    });

    trackRoddoEvent('rewarded_ad_loaded', {
      placement: 'search_lure_reward',
      ad_format: 'rewarded',
      test_mode: isTestMode,
    });

    const reward = await AdMob.showRewardVideoAd({ adId: loaded.adUnitId });
    const rewardAmount = Math.max(0, Number((reward as any)?.amount || 0) || 0);
    const rewardType = String((reward as any)?.type || 'reward');

    trackRoddoEvent('rewarded_ad_completed', {
      placement: 'search_lure_reward',
      ad_format: 'rewarded',
      reward_amount: rewardAmount,
      test_mode: isTestMode,
    });

    if (isTestMode) {
      return {
        ok: true,
        earned: true,
        rewardAmount,
        rewardType,
        testMode: true,
      };
    }

    const verified = await waitForVerifiedReward(requestId);
    trackRoddoEvent('rewarded_ad_ssv_status', {
      placement: 'search_lure_reward',
      ad_format: 'rewarded',
      ssv_status: verified.status,
      granted_amount: verified.grantedAmount || 0,
    });

    return {
      ok: true,
      earned: true,
      rewardAmount: verified.grantedAmount || rewardAmount,
      rewardType,
      testMode: false,
      serverStatus: verified.status,
      searchLureBalance: verified.searchLureBalance,
      dailyCount: verified.dailyCount,
      dailyCap: verified.dailyCap,
    };
  } catch (error: any) {
    const reason = String(error?.code || error?.message || 'rewarded_ad_failed').slice(0, 120);
    trackRoddoEvent('rewarded_ad_failed', {
      placement: 'search_lure_reward',
      ad_format: 'rewarded',
      reason_code: reason,
    });
    if (import.meta.env.DEV) console.warn('[Roddo AdMob] rewarded ad failed', error);
    return { ok: false, earned: false, reason };
  } finally {
    rewardedInFlight = false;
  }
}

// Backward-compatible alias while Zap 2B lands across the UI.
export const showRewardedSearchLureTestAd = showRewardedSearchLureAd;
